'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { useReducedMotion, usePlaybackClock } from '../use-animation-clock';
import {
  channelProducts,
  Vd,
  useMarketTileAnimation,
  ChannelTile,
  ChannelSummary,
  MarketPlaybackControls,
  MarketStepRail,
} from './market-navigation';
import { Ul, MarketReveal, zl, Bl } from './collection-animation';
import { MarketPrompt, MarketImage } from './market-primitives';
import { ShowcaseIcon, useMostlyInView } from './scene-primitives';
import { SegmentedControl, BlurSwap } from './playback-controls';
import { Fd, BuyerPackage, Id } from './buyer-package';
import { LaunchPlan, ListingImages } from './launch-plan';
import { CampaignKit } from './campaign-kit';
import { ProductPage } from './product-page';
import { ProductVideo } from './product-video';
import { MarketingEmail } from './marketing-email';
import { PublishApproval } from './publish-approval';
import { referenceImages } from './reference-brands';
export function ChannelAssortment({ t: e, reduced: t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let [n, r] = React.useState(null);
  React.useEffect(() => {
    if (e !== 0) return;
    const frame = requestAnimationFrame(() => r(null));
    return () => cancelAnimationFrame(frame);
  }, [e]);
  let i = n ?? (t ? 'online' : channelProducts(e)),
    a = Ul.find((e) => e.key === i) ?? Ul[0],
    o = e >= Vd.tiles,
    s = useMarketTileAnimation(o && a ? a.key : 'none', t);
  return a ? (
    <div className={'flex flex-col gap-4'}>
      <MarketPrompt
        t={e}
        workflow={'Create a Channel Assortment'}
        brand={'lisiere'}
        prompt={'FW27 for our own e-commerce, the NY flagship and wholesale'}
        doneAt={Vd.done}
        busy={'Adapting the locked line for 3 channels'}
        result={'3 channel assortments'}
        meta={'Core line unchanged'}
      />
      <MarketReveal
        on={e >= 300}
        className={'mk-card flex flex-col gap-4 p-3 sm:p-4'}
      >
        <div
          className={
            'flex flex-wrap items-center justify-between gap-x-4 gap-y-3'
          }
        >
          <div className={'flex min-w-0 items-center gap-3'}>
            <span
              className={
                'text-ink-3 flex shrink-0 items-center gap-1.5 text-[12px] font-medium'
              }
            >
              <ShowcaseIcon name={'lock'} className={'size-3.5'} />
              <span className={'max-sm:sr-only'}>
                {translateShowcaseNode(showcaseCopy, 'Core line')}
              </span>
            </span>
            <ul
              className={'flex gap-1 sm:gap-1.5'}
              aria-label={translateShowcaseNode(
                showcaseCopy,
                'LISI\xC8RE FW27 core line, locked'
              )}
            >
              {translateShowcaseNode(
                showcaseCopy,
                zl.map((t, n) => {
                  let r = a.ids.includes(t.id);
                  return (
                    <MarketReveal
                      key={t.id}
                      as={'li'}
                      pop={!0}
                      on={e >= Vd.strip}
                      delay={n * 50}
                    >
                      <span
                        className={
                          'relative block transition-opacity duration-300'
                        }
                        style={{
                          opacity: o && !r ? 0.32 : 1,
                        }}
                      >
                        <MarketImage
                          asset={t.asset}
                          alt={translateShowcaseNode(showcaseCopy, t.title)}
                          className={'size-8 rounded-[6px] sm:size-9'}
                        />
                      </span>
                    </MarketReveal>
                  );
                })
              )}
            </ul>
          </div>
          <MarketReveal on={e >= Vd.tabs} className={'max-sm:w-full'}>
            <SegmentedControl
              label={'Channel'}
              size={'sm'}
              value={a.key}
              onChange={r}
              className={'max-sm:w-full max-sm:[&>button]:flex-1'}
              options={Ul.map((e) => ({
                value: e.key,
                label: e.short,
              }))}
            />
          </MarketReveal>
        </div>
        <div className={'h-px bg-[var(--line)]'} />
        <div
          className={'grid gap-4 lg:grid-cols-[minmax(0,1fr)_200px] lg:gap-6'}
        >
          <div ref={s} className={'mk-tiles min-h-[182px] content-start'}>
            {translateShowcaseNode(
              showcaseCopy,
              o
                ? a.ids.map((e, t) => {
                    let n = Bl(e);
                    return n ? (
                      <ChannelTile
                        key={e}
                        piece={n}
                        channel={a}
                        hero={t === 0}
                      />
                    ) : null;
                  })
                : null
            )}
          </div>
          <MarketReveal on={o} delay={200} className={'max-lg:order-first'}>
            <ChannelSummary channel={a} />
          </MarketReveal>
        </div>
        <p className={'text-ink-4 -mt-1 text-[12px]'}>
          {translateShowcaseNode(
            showcaseCopy,
            'Proposed retail \xB7 wholesale about 50% of retail'
          )}
        </p>
      </MarketReveal>
    </div>
  ) : null;
}
export const Kd = Fd[0];
export function useMarketSequence() {
  let e = useReducedMotion(),
    [t, n] = useMostlyInView(0.5),
    [r, i] = React.useState(0),
    [a, o] = React.useState(null),
    [s, c] = React.useState(!0),
    [l, u] = React.useState(!1),
    d = Fd[r] ?? Kd,
    f = d?.ms ?? 1,
    p = usePlaybackClock({
      duration: f,
      running: n === !0 && !l,
      reduced: e,
    }),
    m = (e, t) => {
      (e !== r && (o(r), i(e)), u(!1), p.restart(), t && c(!1));
    };
  const { done, restart } = p;
  React.useEffect(() => {
    if (!done || !s || e) return;
    const frame = requestAnimationFrame(() => {
      if (r < Fd.length - 1) {
        o(r);
        i(r + 1);
        restart();
      } else c(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [done, restart, s, e, r]);
  let h = (t) => {
      let n = Fd[t]?.ms ?? 0;
      return e ? n : t === r ? p.t : t === a ? n : 0;
    },
    g = d?.part ?? 'sell';
  return [
    t,
    {
      index: r,
      part: g,
      auto: s,
      paused: l,
      reduced: e,
      progress: Math.min(1, p.t / f),
      timeFor: h,
      select: (e) => m(e, !0),
      selectPart: (e) => {
        let t = Fd.findIndex((t) => t.part === e);
        t >= 0 && e !== g && m(t, !0);
      },
      togglePause: () => u((e) => !e),
      replay: () => {
        (c(!0), m(0, !1));
      },
    },
  ];
}
export const marketStages = {
  channels: React.memo(ChannelAssortment),
  buyer: React.memo(BuyerPackage),
  launch: React.memo(LaunchPlan),
  campaign: React.memo(CampaignKit),
  listing: React.memo(ListingImages),
  page: React.memo(ProductPage),
  video: React.memo(ProductVideo),
  email: React.memo(MarketingEmail),
  publish: React.memo(PublishApproval),
};
export function useMarketHeight(e) {
  let t = React.useRef(null),
    [n, r] = React.useState(null);
  return (
    React.useLayoutEffect(() => {
      let e = t.current?.querySelector(':scope > [data-active="true"]');
      if (!e) return;
      let n = () => r(e.offsetHeight);
      n();
      let i = new ResizeObserver(n);
      return (i.observe(e), () => i.disconnect());
    }, [e]),
    {
      stage: t,
      height: n,
    }
  );
}
export function SellMarketAnimation() {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let [stageRef, e] = useMarketSequence(),
    { stage: t, height: n } = useMarketHeight(e.index),
    r = Fd[e.index] ?? Fd[0];
  return (
    <div ref={stageRef} className={'sf mt-12 rounded-[20px] p-2 md:mt-14'}>
      <div className={'flex flex-col gap-2 px-1 pt-1 sm:px-2'}>
        <div
          className={
            'flex flex-wrap items-center justify-end gap-2 sm:justify-between'
          }
        >
          <SegmentedControl
            className={'max-sm:w-full max-sm:[&>button]:flex-1'}
            label={'Part'}
            size={'sm'}
            value={e.part}
            onChange={e.selectPart}
            options={Id.map((e) => ({
              value: e.value,
              label: `${e.label} · ${e.brand}`,
            }))}
          />
          <MarketPlaybackControls seq={e} />
        </div>
        <MarketStepRail seq={e} />
        <p
          aria-live={'polite'}
          className={
            'text-ink-3 min-h-10 px-1 pb-1 text-[14px] leading-5 sm:min-h-5'
          }
        >
          {translateShowcaseNode(
            showcaseCopy,
            r?.workflow ? (
              <span className={'ref ref-violet mr-1.5'}>
                <ShowcaseIcon name={'nodes'} className={'ref-icon'} />
                <BlurSwap value={r.workflow} />
              </span>
            ) : (
              <span className={'text-ink mr-1.5 font-medium'}>
                {translateShowcaseNode(showcaseCopy, 'Approval.')}
              </span>
            )
          )}
          <BlurSwap value={r?.does ?? ''} />
        </p>
      </div>
      <div
        className={
          'mt-1 rounded-[12px] bg-[var(--canvas)] p-3 shadow-[inset_0_0_0_1px_var(--line)] md:p-6'
        }
      >
        <div
          ref={t}
          className={'mk-stage'}
          style={{
            height: n ?? void 0,
          }}
        >
          {translateShowcaseNode(
            showcaseCopy,
            Fd.map((t, n) => {
              let Component_r = marketStages[t.key],
                i = n === e.index;
              return (
                <div
                  key={t.key}
                  className={'mk-panel min-w-0'}
                  data-active={i}
                  inert={!i}
                  aria-hidden={!i}
                >
                  <Component_r t={e.timeFor(n)} reduced={e.reduced} />
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
export const J = (e, t) => ({
  'data-on': e >= t ? 'true' : 'false',
});
export function $d(e, t, n) {
  return 1 - (1 - Math.min(1, Math.max(0, (e - t) / n))) ** 3;
}
export const ef = (e, t, n, r) =>
  Math.max(0, Math.min(r, Math.floor((e - t) / n) + 1));
export const tf = (e, t, n, r = 70) =>
  e.slice(0, Math.max(0, Math.floor(((t - n) / 1e3) * r)));
export const rf = referenceImages.orvieto.hero;
export const af = (e, t) => String(e >= t);
export function Render_of() {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={'grid size-full grid-cols-3 gap-px bg-[var(--line)]'}>
      {translateShowcaseNode(
        showcaseCopy,
        referenceImages.orvieto.directions.map((e) => (
          <img
            key={e.src}
            src={e.src}
            alt={translateShowcaseNode(showcaseCopy, '')}
            loading={'lazy'}
            decoding={'async'}
            className={'size-full object-cover'}
          />
        ))
      )}
    </div>
  );
}
